"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { OFFERS_SEARCH_STYLES } from "./constants";
import { resolveOffersSearchTabs } from "./defaultTabs";
import { OffersSearchTabs } from "./OffersSearchTabs";
import type { OffersSearchWidgetProps } from "./types";
import { FlightCtaIcon, CalendarIcon } from "./icons";

function formatMonthYear(dateValue: string, locale = "es-ES"): string {
  if (!dateValue) {
    return "—";
  }

  return new Date(dateValue)
    .toLocaleDateString(locale, { month: "long", year: "numeric" })
    .replace(/^\w/, (character) => character.toUpperCase());
}

export const OffersSearchWidget = React.forwardRef<
  HTMLElement,
  OffersSearchWidgetProps
>(
  (
    {
      className,
      title,
      description,
      imageUrl,
      showImage = true,
      tabs,
      activeTabId,
      defaultDestination,
      defaultDestinationCode: _defaultDestinationCode,
      defaultDestinationCountry: _defaultDestinationCountry,
      defaultAccommodationDestinationId: _defaultAccommodationDestinationId,
      defaultAccommodationDestinationName: _defaultAccommodationDestinationName,
      defaultAccommodationDestinationCountry: _defaultAccommodationDestinationCountry,
      defaultDepartureDate,
      defaultReturnDate,
      fromLabel,
      toLabel,
      ctaLabel,
      ctaHref,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedTabs = resolveOffersSearchTabs(tabs);
    const firstTabId = resolvedTabs[0]?.id;
    const [currentActiveId, setCurrentActiveId] = React.useState(
      activeTabId ?? firstTabId ?? "flights"
    );

    React.useEffect(() => {
      if (activeTabId) {
        setCurrentActiveId(activeTabId);
      }
    }, [activeTabId]);

    const activeTab =
      resolvedTabs.find((tab) => tab.id === currentActiveId) ?? resolvedTabs[0];
    const submitLabel = activeTab?.ctaLabel ?? ctaLabel;
    const submitHref = activeTab?.ctaHref ?? ctaHref;
    const shouldShowImage = showImage && Boolean(imageUrl);

    if (children) {
      return (
        <section
          ref={ref}
          className={cn(OFFERS_SEARCH_STYLES.root, className)}
          {...props}
        >
          <h2 className={OFFERS_SEARCH_STYLES.title}>{title}</h2>
          <div
            className={cn(
              shouldShowImage
                ? OFFERS_SEARCH_STYLES.card
                : OFFERS_SEARCH_STYLES.cardNoImage
            )}
          >
            {shouldShowImage && (
              <div className={OFFERS_SEARCH_STYLES.imageWrapper}>
                <img
                  src={imageUrl}
                  alt={defaultDestination}
                  className={OFFERS_SEARCH_STYLES.image}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <div className={OFFERS_SEARCH_STYLES.form}>{children}</div>
          </div>
        </section>
      );
    }

    return (
      <section
        ref={ref}
        className={cn(OFFERS_SEARCH_STYLES.root, className)}
        {...props}
      >
        <h2 className={OFFERS_SEARCH_STYLES.title}>{title}</h2>

        <form
          action={submitHref}
          method="get"
          className={cn(
            shouldShowImage
              ? OFFERS_SEARCH_STYLES.card
              : OFFERS_SEARCH_STYLES.cardNoImage
          )}
        >
          {shouldShowImage && (
            <div className={OFFERS_SEARCH_STYLES.imageWrapper}>
              <img
                src={imageUrl}
                alt={defaultDestination}
                className={OFFERS_SEARCH_STYLES.image}
                loading="lazy"
                decoding="async"
              />
            </div>
          )}

          <div className={OFFERS_SEARCH_STYLES.form}>
            <OffersSearchTabs
              tabs={resolvedTabs}
              activeTabId={currentActiveId}
              onTabChange={setCurrentActiveId}
              ariaLabel={title}
            />

            <div className={OFFERS_SEARCH_STYLES.inputWrapper}>
              <span className={OFFERS_SEARCH_STYLES.inputIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                  className="text-ticketon-lighter-blue"
                >
                  <path
                    d="M10 11.458a2.625 2.625 0 1 0 0-5.25 2.625 2.625 0 0 0 0 5.25Z"
                    fill="currentColor"
                  />
                  <path
                    d="M10 1.667a6.458 6.458 0 0 0-6.458 6.458c0 4.844 6.458 10.208 6.458 10.208s6.458-5.364 6.458-10.208A6.458 6.458 0 0 0 10 1.667Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <input
                type="text"
                name="destination"
                defaultValue={defaultDestination}
                placeholder={description}
                className={OFFERS_SEARCH_STYLES.input}
              />
            </div>

            <div className={OFFERS_SEARCH_STYLES.dateGrid}>
              <label className={OFFERS_SEARCH_STYLES.dateFieldWrapper}>
                <span className={OFFERS_SEARCH_STYLES.dateFieldIcon}>
                  <CalendarIcon className="h-4 w-4" />
                </span>
                <span className={OFFERS_SEARCH_STYLES.dateFieldContent}>
                  <span className={OFFERS_SEARCH_STYLES.dateFieldLabel}>
                    {fromLabel}
                  </span>
                  <span className={OFFERS_SEARCH_STYLES.dateFieldValue}>
                    {formatMonthYear(defaultDepartureDate)}
                  </span>
                </span>
                <svg
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={OFFERS_SEARCH_STYLES.dateFieldChevron}
                  aria-hidden
                >
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="#0079D9"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="date"
                  name="from"
                  defaultValue={defaultDepartureDate}
                  className={OFFERS_SEARCH_STYLES.dateInput}
                />
              </label>
              <label className={OFFERS_SEARCH_STYLES.dateFieldWrapper}>
                <span className={OFFERS_SEARCH_STYLES.dateFieldIcon}>
                  <CalendarIcon className="h-4 w-4" />
                </span>
                <span className={OFFERS_SEARCH_STYLES.dateFieldContent}>
                  <span className={OFFERS_SEARCH_STYLES.dateFieldLabel}>
                    {toLabel}
                  </span>
                  <span className={OFFERS_SEARCH_STYLES.dateFieldValue}>
                    {formatMonthYear(defaultReturnDate)}
                  </span>
                </span>
                <svg
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={OFFERS_SEARCH_STYLES.dateFieldChevron}
                  aria-hidden
                >
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="date"
                  name="to"
                  defaultValue={defaultReturnDate}
                  className={OFFERS_SEARCH_STYLES.dateInput}
                />
              </label>
            </div>

            <button type="submit" className={OFFERS_SEARCH_STYLES.cta}>
              <FlightCtaIcon
                className={cn(OFFERS_SEARCH_STYLES.ctaIcon, "text-white")}
              />
              {submitLabel}
            </button>
          </div>
        </form>
      </section>
    );
  }
);

OffersSearchWidget.displayName = "OffersSearchWidget";
